import React, { FC, SyntheticEvent, useState } from 'react';
import styles from './UserDescription.module.scss';
import { useHistory } from 'react-router';
import { v4 as uuid } from 'uuid';
import { User, Action, ArrowDown, Button } from 'esa-ui-library';
import { format } from 'date-fns';
import { PropertyType as PT, useStore, flattenPartners, PROFILE_SETTINGS_URL } from '../../../../../common';

interface UserDescriptionProps {
	log: PT;
	order: number;
}

const UserDescription: FC<UserDescriptionProps> = ({ log, order }): JSX.Element => {
	const [partnersOpened, setPartnersOpened] = useState(false);
	const [{ partners }] = useStore();
	const history = useHistory();
	const { aggregator_user_id, profile, username } = log.user;
	const { updated_users, partner_ids } = log.properties;

	const image = profile.public_image_url;

	const onActionClick = (e: SyntheticEvent) => {
		e.stopPropagation();
	};

	const onPartnersToggle = () => {
		setPartnersOpened((prev) => !prev);
	};

	const onUserOpen = (id: string) => {
		history.push(`${PROFILE_SETTINGS_URL.value}/${id}`);
	};

	const renderPartners = () => {
		const activePartners = flattenPartners(partner_ids, partners).filter(
			(p: PT) => partner_ids.includes(p.value) || p.active
		);

		return (
			<ul className={styles.PartnerList}>
				{activePartners.map((p: PT) => (
					<li key={uuid()} className={styles.Partner}>
						{p.label} {!!p.options.length && <span className={styles.Count}>({p.active})</span>}
					</li>
				))}
			</ul>
		);
	};

	const renderUserDetails = (user: PT) => {
		const { profile, username, email, last_activity_at } = user;
		const image = profile.public_image_url;

		return (
			<section className={styles.UserDetails} onClick={onActionClick}>
				<section className={styles.PersonalDetails}>
					{image && <img className={styles.UserDetailsImage} src={image} alt='' />}
					{!image && <User className={styles.UserDetailsIcon} />}
					<section>
						<div className={styles.UserName}>{username}</div>
						<div className={styles.UserEmail}>{email}</div>
						<Button
							small
							primary
							title='View User'
							className={styles.ViewButton}
							onClick={() => onUserOpen(aggregator_user_id)}
						/>
					</section>
				</section>
				<section className={styles.LastActivity}>
					<div className={styles.ActivityLabel}>Last Active</div>
					<div>{format(new Date(last_activity_at), 'EEEE, MMM dd yyyy')}</div>
					<div className={styles.ActivityTime}>{format(new Date(last_activity_at), 'HH:mm aa')}</div>
				</section>
				<section className={styles.Partners}>
					<div className={styles.PartnersTitle} onClick={onPartnersToggle}>
						Partners <ArrowDown />
					</div>
					{partnersOpened && renderPartners()}
				</section>
			</section>
		);
	};

	const renderUpdatedDetails = (users: PT[]) => {
		const additionalUsers = [...users];
		additionalUsers.shift();

		return (
			<Action label={`+${additionalUsers?.length} others`} noArrow>
				<ul className={styles.AdditionalUsers}>
					{additionalUsers?.map((u: PT) => (
						<li key={uuid()} className={styles.AdditionalUser} onClick={() => onUserOpen(u.profile?.user_id)}>
							{u.profile?.public_image_url && (
								<img className={styles.UserImage} src={u.profile?.public_image_url} alt='' />
							)}
							{!u.profile?.public_image_url && <User className={styles.UserIcon} />}
							<div className={styles.UserName}>{u.username}</div>
						</li>
					))}
				</ul>
			</Action>
		);
	};

	const renderFirstUser = (user: PT) => (
		<>
			<span className={styles.FirstUser} onClick={() => onUserOpen(user.profile.user_id)}>
				{user.username}
			</span>
		</>
	);

	const renderUpdatedUsers = (users: PT[]) => {
		if (users.length < 2) return renderFirstUser(users[0]);

		return (
			<>
				{renderFirstUser(users[0])} {renderUpdatedDetails(users)}
			</>
		);
	};

	return (
		<section className={styles.Description}>
			{image && <img className={styles.UserImage} src={image} alt='' />}
			{!image && <User className={styles.UserIcon} />}
			{
				<Action label={username} noArrow dropdownClass={order > 4 ? styles.DropUp : ''}>
					{renderUserDetails(log.user)}
				</Action>
			}
			{log.description}
			{updated_users?.length && renderUpdatedUsers(updated_users)}
		</section>
	);
};

export { UserDescription };
